import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Edit, Trash2, Package, ChevronRight, AlertCircle, Layers, Eye, X, Filter, ChevronDown, Copy, Download, Upload, AlertTriangle } from 'lucide-react';

import { API_BASE } from '../lib/apiBase';
import ProductFamilyTabs, { getProductFamilyMeta } from '../components/ProductFamilyTabs';
import DuplicateToFamilyDialog from '../components/DuplicateToFamilyDialog';
import FamilyScopePicker, { FamilyBadge, CrossFamilyWarning } from '../components/FamilyScopePicker';
const API_URL = API_BASE;

/**
 * SKUSelector - Enhanced SKU selector with category filtering and search
 * Features:
 * - Category filter dropdown
 * - Search within filtered results
 * - Grouped display by category
 * - Click-outside to close
 */
function SKUSelector({
  value,
  items,
  onChange,
  placeholder = "Select SKU...",
  productFamily = null,
  activeFamily = 'vinyl',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Get unique categories from items
  const categories = useMemo(() => {
    const cats = [...new Set(items.map(item => item.category).filter(Boolean))];
    return cats.sort();
  }, [items]);

  // Filter and group items
  const filteredItems = useMemo(() => {
    let filtered = items;
    
    // Apply category filter
    if (categoryFilter) {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }
    
    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item => 
        item.supplier_sku?.toLowerCase().includes(searchLower) ||
        item.name?.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [items, categoryFilter, search]);

  // Group filtered items by category
  const groupedItems = useMemo(() => {
    const groups = {};
    filteredItems.forEach(item => {
      const cat = item.category || 'Uncategorized';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  }, [filteredItems]);

  // Find selected item
  const selectedItem = items.find(item => item.supplier_sku === value);

  // Calculate dropdown position when opening - flip up if not enough space below
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = 400; // Max height of dropdown
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Flip upward if not enough space below but enough space above
      const shouldFlipUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      
      setDropdownPosition({
        top: shouldFlipUp ? rect.top - Math.min(dropdownHeight, spaceAbove - 10) : rect.bottom + 4,
        left: rect.left,
        width: 400,
        maxHeight: shouldFlipUp ? Math.min(dropdownHeight, spaceAbove - 10) : Math.min(dropdownHeight, spaceBelow - 10)
      });
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        // Check if click is on the dropdown (which is in a portal)
        const dropdown = document.getElementById('sku-dropdown-portal');
        if (dropdown && dropdown.contains(e.target)) return;
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search when opening
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const searchInput = document.getElementById('sku-search-input');
        if (searchInput) searchInput.focus();
      }, 50);
    }
  }, [isOpen]);

  const handleSelect = (item) => {
    onChange(item.supplier_sku, item);
    setIsOpen(false);
    setSearch('');
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onChange('', null);
  };

  return (
    <div ref={containerRef} className="relative flex-1">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between border rounded px-2 py-1 text-sm text-left
          ${isOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'}
          ${!value ? 'text-gray-400' : 'text-gray-900'}`}
      >
        <span className="truncate flex-1 flex items-center gap-1.5">
          <span className="truncate">
            {selectedItem 
              ? `${selectedItem.supplier_sku} - ${selectedItem.name}`
              : placeholder
            }
          </span>
          {selectedItem?.product_family && (
            <CrossFamilyWarning
              valueFamily={selectedItem.product_family}
              activeFamily={activeFamily}
            />
          )}
          {selectedItem?.is_quotable === false && (
            // Inline "Quote Required" chip in the component row — stays
            // visible after the picker closes so the admin always sees
            // which rows will cascade the Request-a-Quote workflow.
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200 flex-shrink-0"
              data-testid={`component-quote-required-${selectedItem.supplier_sku}`}
            >
              Quote Required
            </span>
          )}
        </span>
        <div className="flex items-center gap-1 ml-2">
          {value && (
            <X 
              className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" 
              onClick={clearSelection}
            />
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Dropdown - Fixed position to escape modal overflow */}
      {isOpen && ReactDOM.createPortal(
        <div 
          id="sku-dropdown-portal"
          className="bg-white border border-gray-200 rounded-lg shadow-2xl overflow-hidden"
          style={{ 
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 9999,
            maxHeight: dropdownPosition.maxHeight || 400
          }}
        >
          {/* Search and Filter Header */}
          <div className="p-2 border-b border-gray-100 space-y-2 bg-white">
            {/* Always show what family is currently driving the loaded items.
                The actual scope picker is at the BOM modal header level so
                a native <select> overlay won't accidentally collapse this dropdown. */}
            {productFamily && (
              <div className="flex items-center gap-1.5 px-1 text-xs text-slate-600">
                <span className="text-slate-500">Items from</span>
                <FamilyBadge family={productFamily} />
                <span className="text-slate-400">— change in modal header</span>
              </div>
            )}
            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="flex-1 border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
              >
                <option value="">All Categories ({items.length})</option>
                {categories.map(cat => {
                  const count = items.filter(i => i.category === cat).length;
                  return (
                    <option key={cat} value={cat}>
                      {cat} ({count})
                    </option>
                  );
                })}
              </select>
            </div>
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="sku-search-input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search SKU or name..."
                className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-blue-400"
              />
            </div>
          </div>

          {/* Items List */}
          <div style={{ maxHeight: (dropdownPosition.maxHeight || 400) - 120, overflowY: 'auto' }}>
            {Object.keys(groupedItems).length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                No items match your search
              </div>
            ) : (
              Object.entries(groupedItems).map(([category, categoryItems]) => (
                <div key={category}>
                  {/* Category Header */}
                  <div className="px-3 py-1.5 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 border-b border-gray-100">
                    {category} ({categoryItems.length})
                  </div>
                  {/* Category Items */}
                  {categoryItems.map(item => {
                    const isQuoteRequired = item.is_quotable === false;
                    return (
                    <button
                      key={item.supplier_sku}
                      type="button"
                      onClick={() => handleSelect(item)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center justify-between border-b border-gray-50 last:border-0
                        ${value === item.supplier_sku ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                      data-testid={`sku-picker-row-${item.supplier_sku}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs text-gray-500 flex items-center gap-1.5 flex-wrap">
                          <span>{item.supplier_sku}</span>
                          {item.product_family && (
                            <FamilyBadge family={item.product_family} />
                          )}
                          {isQuoteRequired && (
                            // Admin preview badge — mirrors the backend
                            // ``is_quotable=false`` → "Quote Required"
                            // display from the Procurement Catalog UI so
                            // the admin sees the same signal in both
                            // places. Informational only; selecting the
                            // item is still allowed per the ask.
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                              data-testid={`sku-picker-quote-required-${item.supplier_sku}`}
                            >
                              Quote Required
                            </span>
                          )}
                        </div>
                        <div className="truncate">{item.name}</div>
                      </div>
                      {value === item.supplier_sku && (
                        <div className="w-2 h-2 rounded-full bg-blue-500 ml-2 flex-shrink-0" />
                      )}
                    </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer with count */}
          <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
            {filteredItems.length} of {items.length} items
            {categoryFilter && ` in "${categoryFilter}"`}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function BOMManagementPage() {
  const navigate = useNavigate();
  const [boms, setBoms] = useState([]);
  const [selectedBom, setSelectedBom] = useState(null);
  // Server-authoritative gate state for the selected BOM — populated by
  // the Cost Preview panel via its ``onGateChange`` callback. Includes
  // nested-BOM hits that the local ``quoteRequiredSkusForBom`` cannot
  // see (it only walks the saved BOM's direct components). We overlay
  // the two so the header badge is always a superset.
  const [selectedBomGate, setSelectedBomGate] = useState({ quote_required: false, components: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBom, setEditingBom] = useState(null);
  // Non-null while cloning: seeds the modal with the source BOM's data
  // but keeps the form in CREATE mode (editable Product ID, POST on save).
  const [cloneSource, setCloneSource] = useState(null);

  // Phase 2: product family scoping
  const [productFamily, setProductFamily] = useState('vinyl');
  const [familyCounts, setFamilyCounts] = useState({ vinyl: 0, cd: 0, dvd: 0, bluray: 0 });

  // Duplicate-to-family
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateSourceBom, setDuplicateSourceBom] = useState(null);
  const [duplicateBusy, setDuplicateBusy] = useState(false);

  // Procurement items for component SKU lookup
  const [procurementItems, setProcurementItems] = useState([]);
  // Phase 2 soft scoping: default to the active family but admin can widen.
  const [skuScope, setSkuScope] = useState('current');

  // Import / Export state
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('create_and_update');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const getToken = () => localStorage.getItem('token');

  const handleExport = async (fileFormat, scope) => {
    setShowExportMenu(false);
    try {
      const family = scope === 'all' ? 'all' : productFamily;
      const res = await fetch(
        `${API_URL}/api/bom/export?product_family=${encodeURIComponent(family)}&file_format=${fileFormat}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      // Parse filename from Content-Disposition
      const disp = res.headers.get('Content-Disposition') || '';
      const m = disp.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `product_boms.${fileFormat}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + err.message);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await fetch(
        `${API_URL}/api/bom/import?mode=${encodeURIComponent(importMode)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` },
          body: fd,
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setImportResult(data);
      // Refresh lists after a successful mutation
      fetchBoms();
      fetchFamilyCounts();
    } catch (err) {
      setImportResult({ errors: [err.message], total_errors: 1 });
    } finally {
      setImporting(false);
    }
  };

  const fetchBoms = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_URL}/api/bom/products?product_family=${encodeURIComponent(productFamily)}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      if (!res.ok) throw new Error('Failed to fetch BOMs');
      const data = await res.json();
      setBoms(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [productFamily]);

  const fetchFamilyCounts = useCallback(async () => {
    const families = ['vinyl', 'cd', 'dvd', 'bluray'];
    try {
      const results = await Promise.all(
        families.map(fam =>
          fetch(`${API_URL}/api/bom/products?product_family=${fam}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
          })
            .then(r => r.ok ? r.json() : [])
            .then(d => [fam, (d || []).length])
            .catch(() => [fam, 0])
        )
      );
      setFamilyCounts(Object.fromEntries(results));
    } catch (err) {
      console.error('Failed to fetch BOM family counts:', err);
    }
  }, []);

  const fetchProcurementItems = useCallback(async () => {
    try {
      // Phase 2 (soft scoping): resolve the chosen scope to an API param.
      //   'current' → ?product_family={activeFamily}
      //   'all'     → ?product_family=all (backend bypasses filter)
      //   <family>  → ?product_family=<family>
      const resolvedFamily = skuScope === 'current' ? productFamily : skuScope;
      const res = await fetch(
        `${API_URL}/api/pricing/items?limit=500&product_family=${encodeURIComponent(resolvedFamily)}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      if (res.ok) {
        const data = await res.json();
        // Map price_items format to procurement format for compatibility
        const items = (data.items || []).map(item => ({
          ...item,
          supplier_sku: item.sku, // Map sku to supplier_sku for BOM compatibility
        }));
        setProcurementItems(items);
      }
    } catch (err) {
      console.error('Failed to fetch procurement items:', err);
    }
  }, [productFamily, skuScope]);

  useEffect(() => {
    fetchBoms();
    fetchFamilyCounts();
    fetchProcurementItems();
  }, [fetchBoms, fetchFamilyCounts, fetchProcurementItems]);

  const handleDelete = async (bomId) => {
    if (!window.confirm('Delete this BOM? This cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bom/products/${bomId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete');
      }
      
      if (selectedBom?.id === bomId) setSelectedBom(null);
      fetchBoms();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleClone = (bom) => {
    // Client-side clone: open the create modal pre-filled from the source
    // BOM so the admin can pick their own Product ID before saving.
    // No server-side write happens until they click Save — which POSTs a
    // brand-new BOM (independent from the source). Canceling discards
    // the draft entirely, leaving the original untouched.
    setEditingBom(null);
    // Suggest a unique-ish product_id derived from the source (`-COPY`,
    // then `-COPY-2`, `-COPY-3`, …) that doesn't collide with any BOM
    // currently in memory. If the admin ignores the suggestion and picks
    // their own, the backend uniqueness check will still catch collisions.
    const base = (bom.product_id || 'product').trim();
    let candidate = `${base}-COPY`;
    let n = 2;
    const existingIds = new Set(boms.map(b => b.product_id));
    while (existingIds.has(candidate)) {
      candidate = `${base}-COPY-${n}`;
      n += 1;
    }
    setCloneSource({
      ...bom,
      product_id: candidate,
      name: `${bom.name || ''} (Copy)`.trim(),
    });
    setShowModal(true);
  };

  const filteredBoms = boms.filter(bom =>
    bom.product_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bom.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getProcurementItemName = (sku) => {
    const item = procurementItems.find(i => i.supplier_sku === sku);
    return item?.name || sku;
  };

  // ---- Shared "Quote Required" state --------------------------------
  // Direct (non-nested) detection reads straight from the already-loaded
  // procurementItems payload — the same source the editor banner uses,
  // so the saved detail view, left-hand list and cost preview can never
  // disagree. Nested-BOM detection is deferred to the backend's cost
  // response (``quote_required_components``) — we overlay those hits
  // on top of the client-side result inside the detail panel.
  const procurementBySkuAll = useMemo(() => {
    const m = new Map();
    for (const it of procurementItems || []) {
      if (it && it.supplier_sku) m.set(it.supplier_sku, it);
    }
    return m;
  }, [procurementItems]);

  const quoteRequiredSkusForBom = useCallback((bom) => {
    const hits = [];
    const seen = new Set();
    for (const c of bom?.components || []) {
      if (seen.has(c.supplier_sku)) continue;
      const item = procurementBySkuAll.get(c.supplier_sku);
      if (item && item.is_quotable === false) {
        seen.add(c.supplier_sku);
        hits.push({
          supplier_sku: c.supplier_sku,
          procurement_item_name: item.name || c.supplier_sku,
          applicability: c.applicability || 'both',
        });
      }
    }
    return hits;
  }, [procurementBySkuAll]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/pricing')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Product BOM Management</h1>
              <p className="text-sm text-gray-500">
                Define product recipes - what procurement components make up each sellable product
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Phase 2: Product family tabs */}
        <div className="mb-4 flex items-center justify-between">
          <ProductFamilyTabs
            value={productFamily}
            onChange={(fam) => {
              setProductFamily(fam);
              setSelectedBom(null);
            }}
            counts={familyCounts}
          />
          <span className="text-xs text-slate-500">
            Showing <span className="font-medium text-slate-700">{getProductFamilyMeta(productFamily).label}</span> BOMs
          </span>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* BOM List */}
          <div className="col-span-5">
            <div className="bg-white rounded-lg border border-border">
              <div className="px-4 py-3 border-b border-border">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">
                    Product BOMs ({boms.length})
                    <span className="ml-2 text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full uppercase">
                      {getProductFamilyMeta(productFamily).label}
                    </span>
                  </h3>
                  <div className="flex items-center gap-2 relative">
                    <button
                      onClick={() => setShowImportModal(true)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                      data-testid="import-boms-btn"
                      title="Import BOMs from xlsx or csv"
                    >
                      <Upload className="w-4 h-4" />
                      Import
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setShowExportMenu(v => !v)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                        data-testid="export-boms-btn"
                        title="Export BOMs to xlsx or csv"
                      >
                        <Download className="w-4 h-4" />
                        Export
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      {showExportMenu && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                          <div
                            className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1"
                            data-testid="export-menu"
                          >
                            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                              {getProductFamilyMeta(productFamily).label} only
                            </div>
                            <button
                              onClick={() => handleExport('xlsx', 'current')}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2"
                              data-testid="export-current-xlsx"
                            >
                              <Download className="w-3.5 h-3.5 text-slate-400" />
                              Excel (.xlsx)
                            </button>
                            <button
                              onClick={() => handleExport('csv', 'current')}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2"
                              data-testid="export-current-csv"
                            >
                              <Download className="w-3.5 h-3.5 text-slate-400" />
                              CSV (.csv)
                            </button>
                            <div className="border-t my-1"></div>
                            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                              All product types
                            </div>
                            <button
                              onClick={() => handleExport('xlsx', 'all')}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2"
                              data-testid="export-all-xlsx"
                            >
                              <Download className="w-3.5 h-3.5 text-slate-400" />
                              Excel (.xlsx)
                            </button>
                            <button
                              onClick={() => handleExport('csv', 'all')}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2"
                              data-testid="export-all-csv"
                            >
                              <Download className="w-3.5 h-3.5 text-slate-400" />
                              CSV (.csv)
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => { setEditingBom(null); setShowModal(true); }}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                      data-testid="new-bom-btn"
                    >
                      <Plus className="w-4 h-4" />
                      New BOM
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search BOMs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                {filteredBoms.length === 0 ? (
                  searchTerm ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      No BOMs match your search
                    </div>
                  ) : (
                    <BOMEmptyState
                      family={productFamily}
                      onCreateNew={() => { setEditingBom(null); setShowModal(true); }}
                      onPickVinylToDuplicate={async () => {
                        try {
                          const r = await fetch(`${API_URL}/api/bom/products?product_family=vinyl`, {
                            headers: { 'Authorization': `Bearer ${getToken()}` }
                          });
                          if (!r.ok) throw new Error('Failed to fetch Vinyl BOMs');
                          const data = await r.json();
                          if (!data || data.length === 0) {
                            alert('No Vinyl BOMs exist to duplicate from.');
                            return;
                          }
                          setDuplicateSourceBom(data[0]);
                          setShowDuplicateModal(true);
                        } catch (err) {
                          alert(`Could not load Vinyl BOMs: ${err.message}`);
                        }
                      }}
                    />
                  )
                ) : (
                  filteredBoms.map(bom => {
                    const bomQuoteHits = quoteRequiredSkusForBom(bom);
                    return (
                    <div
                      key={bom.id}
                      onClick={() => { setSelectedBom(bom); setSelectedBomGate({ quote_required: false, components: [] }); }}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                        selectedBom?.id === bom.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                      }`}
                      data-testid={`bom-row-${bom.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 flex items-center gap-1.5">
                            <span className="truncate">{bom.name}</span>
                            {bomQuoteHits.length > 0 && (
                              // Compact list indicator — single pill, no
                              // counter clutter. Hover-title lists the
                              // SKU names so the admin can see WHICH
                              // component triggered the gate without
                              // opening the BOM.
                              <span
                                title={`Quote Required: ${bomQuoteHits.map(h => h.supplier_sku).join(', ')}`}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200 flex-shrink-0"
                                data-testid={`bom-list-quote-required-${bom.id}`}
                              >
                                Quote Required
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 font-mono">{bom.product_id}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            {bom.components?.length || 0} components
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleClone(bom); }}
                            className="p-1 hover:bg-emerald-50 rounded text-emerald-600"
                            title="Clone BOM (same family)"
                            data-testid={`clone-bom-${bom.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDuplicateSourceBom(bom); setShowDuplicateModal(true); }}
                            className="p-1 hover:bg-indigo-50 rounded text-indigo-600"
                            title="Duplicate to family"
                            data-testid={`duplicate-bom-${bom.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* BOM Details */}
          <div className="col-span-7">
            {selectedBom ? (() => {
              // Combine direct-level (from already-loaded procurement
              // items) with server-authoritative gate state (includes
              // nested-BOM detection). Dedup by supplier_sku.
              const directHits = quoteRequiredSkusForBom(selectedBom);
              const serverHits = selectedBomGate.components || [];
              const combinedMap = new Map();
              [...directHits, ...serverHits].forEach(h => {
                if (!combinedMap.has(h.supplier_sku)) combinedMap.set(h.supplier_sku, h);
              });
              const combinedHits = Array.from(combinedMap.values());
              const quoteRequiredSkuSet = new Set(combinedHits.map(h => h.supplier_sku));
              const gateActive = combinedHits.length > 0 || selectedBomGate.quote_required;
              return (
              <div className="bg-white rounded-lg border border-border">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <div className="min-w-0">
                    <h3 className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                      <span>{selectedBom.name}</span>
                      {gateActive && (
                        // Header badge — same wording / colour as the
                        // editor banner, left-hand list chip and cost
                        // preview footer so admins recognise the state
                        // without opening Edit.
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200"
                          data-testid="bom-detail-quote-required-badge"
                          title={`Triggered by: ${combinedHits.map(h => h.supplier_sku).join(', ')}`}
                        >
                          Quote Required
                        </span>
                      )}
                    </h3>
                    <div className="text-sm text-gray-500 font-mono">{selectedBom.product_id}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setEditingBom(selectedBom); setShowModal(true); }}
                      className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleClone(selectedBom)}
                      className="flex items-center gap-1 px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-lg text-sm hover:bg-emerald-50"
                      title="Clone this BOM"
                      data-testid={`clone-bom-details-${selectedBom.id}`}
                    >
                      <Copy className="w-4 h-4" />
                      Clone
                    </button>
                    <button
                      onClick={() => handleDelete(selectedBom.id)}
                      className="flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>

                {gateActive && combinedHits.length > 0 && (
                  // Informational list of the exact affected components
                  // so admins don't need to scan the Cost Preview table.
                  <div
                    className="px-4 py-2 border-b border-yellow-200 bg-yellow-50 text-xs text-yellow-900"
                    data-testid="bom-detail-quote-required-list"
                  >
                    <span className="font-semibold">Affected components: </span>
                    {combinedHits.map((h, i) => (
                      <span key={h.supplier_sku}>
                        {i > 0 && ', '}
                        <span
                          className="font-mono"
                          data-testid={`bom-detail-quote-required-sku-${h.supplier_sku}`}
                        >
                          {h.supplier_sku}
                        </span>
                        {h.procurement_item_name ? ` (${h.procurement_item_name})` : ''}
                      </span>
                    ))}
                  </div>
                )}

                {selectedBom.description && (
                  <div className="px-4 py-3 border-b border-gray-100 text-sm text-gray-600">
                    {selectedBom.description}
                  </div>
                )}

                <div className="px-4 py-3 border-b border-gray-100 grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Format:</span>
                    <span className="ml-2 font-medium">{selectedBom.format || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Variant:</span>
                    <span className="ml-2 font-medium">{selectedBom.variant || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Product Family:</span>
                    <span className="ml-2">
                      <FamilyBadge family={selectedBom.product_family || selectedBom.category || 'vinyl'} />
                    </span>
                  </div>
                </div>

                {/* Cost Preview with Component Breakdown */}
                <div className="p-4">
                  <CostPreviewWithBreakdown 
                    bomProductId={selectedBom.product_id} 
                    components={selectedBom.components}
                    getProcurementItemName={getProcurementItemName}
                    onGateChange={setSelectedBomGate}
                  />
                </div>
              </div>
              );
            })() : (
              <div className="bg-white rounded-lg border border-border p-8 text-center text-gray-500">
                <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Select a BOM to view its components</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* BOM Modal */}
      {showModal && (
        <BOMModal
          bom={editingBom}
          cloneSource={cloneSource}
          procurementItems={procurementItems}
          productFamily={productFamily}
          skuScope={skuScope}
          onSkuScopeChange={setSkuScope}
          onSave={() => { setShowModal(false); setEditingBom(null); setCloneSource(null); fetchBoms(); fetchFamilyCounts(); }}
          onClose={() => { setShowModal(false); setEditingBom(null); setCloneSource(null); }}
        />
      )}

      {/* Duplicate-to-Family Dialog */}
      <DuplicateToFamilyDialog
        open={showDuplicateModal}
        onClose={() => { setShowDuplicateModal(false); setDuplicateSourceBom(null); }}
        sourceLabel={duplicateSourceBom?.name || ''}
        sourceFamily={duplicateSourceBom?.product_family || 'vinyl'}
        entityType="bom"
        busy={duplicateBusy}
        onConfirm={async (payload) => {
          if (!duplicateSourceBom) return;
          setDuplicateBusy(true);
          try {
            const r = await fetch(
              `${API_URL}/api/bom/products/${duplicateSourceBom.id}/duplicate-to-family`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${getToken()}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
              }
            );
            if (!r.ok) {
              const err = await r.json();
              throw new Error(err.detail || 'Duplicate failed');
            }
            const result = await r.json();
            setShowDuplicateModal(false);
            setDuplicateSourceBom(null);
            setProductFamily(payload.product_family);
            fetchFamilyCounts();
            const msg = [
              `New ${payload.product_family.toUpperCase()} BOM: "${result.new_name}"`,
              `Components copied: ${result.components_copied}`,
              result.components_needing_review > 0
                ? `${result.components_needing_review} component(s) need review`
                : null,
            ].filter(Boolean).join('\n');
            alert(msg);
          } catch (err) {
            alert(`Duplicate failed: ${err.message}`);
          } finally {
            setDuplicateBusy(false);
          }
        }}
      />

      {/* Import BOMs Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="import-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Import BOMs</h3>
                <p className="text-xs text-slate-500 mt-0.5">Upload an .xlsx or .csv file — one row per BOM component.</p>
              </div>
              <button
                onClick={() => { setShowImportModal(false); setImportFile(null); setImportResult(null); }}
                className="p-1 hover:bg-slate-100 rounded"
                data-testid="import-modal-close"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {!importResult && (
                <>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">File</label>
                    <input
                      type="file"
                      accept=".xlsx,.csv"
                      onChange={e => setImportFile(e.target.files?.[0] || null)}
                      className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                      data-testid="import-file-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">Tip: run Export first to get a template with the exact columns.</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">Update mode</label>
                    <div className="space-y-1.5">
                      {[
                        { value: 'create_only', label: 'Create new only', hint: 'Skip BOMs that already exist' },
                        { value: 'create_and_update', label: 'Create and update', hint: 'Update BOM-level fields on existing BOMs — components are left untouched' },
                        { value: 'add_missing_components', label: 'Add missing components only', hint: 'Append components whose SKU is not already on the existing BOM' },
                        { value: 'replace_components', label: 'Replace existing BOM components', hint: 'Danger: replaces the entire component list with what is in the file. Any component not in the file is removed.', danger: true },
                      ].map(opt => (
                        <label
                          key={opt.value}
                          className={`flex items-start gap-2 p-2.5 border rounded cursor-pointer text-sm ${
                            importMode === opt.value
                              ? (opt.danger ? 'border-red-400 bg-red-50' : 'border-blue-400 bg-blue-50')
                              : 'border-slate-200 hover:bg-slate-50'
                          }`}
                          data-testid={`import-mode-${opt.value}`}
                        >
                          <input
                            type="radio"
                            name="import-mode"
                            value={opt.value}
                            checked={importMode === opt.value}
                            onChange={e => setImportMode(e.target.value)}
                            className="mt-0.5"
                          />
                          <div className="flex-1">
                            <div className={`font-medium ${opt.danger ? 'text-red-700' : 'text-slate-800'}`}>
                              {opt.label}
                            </div>
                            <div className={`text-xs ${opt.danger ? 'text-red-600' : 'text-slate-500'}`}>
                              {opt.hint}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {importMode === 'replace_components' && (
                    <div className="rounded-md border border-red-300 bg-red-50 p-3 flex items-start gap-2" data-testid="import-danger-warning">
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                      <div className="text-xs text-red-700">
                        <strong>Destructive action.</strong> For each existing BOM in your file, the current component list will be REPLACED with what is in the file. Any component you did not include will be removed. This cannot be undone.
                      </div>
                    </div>
                  )}
                </>
              )}

              {importResult && (
                <div className="space-y-3" data-testid="import-result">
                  <div className="grid grid-cols-2 gap-2">
                    <ResultStat label="Created" value={importResult.created ?? 0} tone="emerald" />
                    <ResultStat label="Updated" value={importResult.updated ?? 0} tone="blue" />
                    <ResultStat label="Skipped" value={importResult.skipped ?? 0} tone="slate" />
                    <ResultStat
                      label={importMode === 'replace_components' ? 'Components replaced' : 'Components added'}
                      value={importMode === 'replace_components' ? (importResult.components_replaced ?? 0) : (importResult.components_added ?? 0)}
                      tone="indigo"
                    />
                  </div>
                  {importResult.total_errors > 0 && (
                    <div className="rounded border border-amber-300 bg-amber-50 p-2.5">
                      <div className="text-xs font-semibold text-amber-800 mb-1">
                        {importResult.total_errors} error{importResult.total_errors === 1 ? '' : 's'}
                      </div>
                      <ul className="text-xs text-amber-700 space-y-0.5 max-h-40 overflow-y-auto">
                        {(importResult.errors || []).map((e, i) => (
                          <li key={i} className="font-mono">• {e}</li>
                        ))}
                        {importResult.total_errors > (importResult.errors?.length || 0) && (
                          <li className="italic">… and {importResult.total_errors - (importResult.errors?.length || 0)} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
              {importResult ? (
                <button
                  onClick={() => { setShowImportModal(false); setImportFile(null); setImportResult(null); }}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  data-testid="import-close-btn"
                >
                  Done
                </button>
              ) : (
                <>
                  <button
                    onClick={() => { setShowImportModal(false); setImportFile(null); }}
                    className="px-3 py-1.5 text-sm border border-slate-300 text-slate-700 rounded hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={!importFile || importing}
                    className={`px-3 py-1.5 text-sm text-white rounded ${
                      importMode === 'replace_components' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid="import-submit-btn"
                  >
                    {importing ? 'Importing…' : (importMode === 'replace_components' ? 'Import (replace)' : 'Import')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultStat({ label, value, tone = 'slate' }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    slate: 'bg-slate-50 text-slate-800 border-slate-200',
    indigo: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  };
  return (
    <div className={`border rounded px-3 py-2 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function BOMEmptyState({ family, onCreateNew, onPickVinylToDuplicate }) {
  const meta = getProductFamilyMeta(family);
  const Icon = meta.icon;
  const isVinyl = family === 'vinyl';
  return (
    <div className="px-6 py-12 text-center" data-testid={`bom-empty-family-${family}`}>
      <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-100 rounded-full mb-3">
        <Icon className="w-6 h-6 text-slate-500" />
      </div>
      <p className="text-sm font-medium text-slate-900">
        No {meta.label} BOMs yet
      </p>
      <p className="text-xs text-slate-500 mt-1">
        {isVinyl
          ? 'Create your first BOM to define product recipes.'
          : `Start a ${meta.label} setup — either fresh or based on your Vinyl template.`}
      </p>
      <div className="mt-4 flex items-center justify-center gap-2">
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create your first {meta.label} BOM
        </button>
        {!isVinyl && (
          <button
            onClick={onPickVinylToDuplicate}
            className="px-4 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center gap-2"
          >
            <Copy className="w-4 h-4" />
            Duplicate from Vinyl
          </button>
        )}
      </div>
    </div>
  );
}

// Enhanced Cost Preview Component with Component Breakdown.
// ``onGateChange(gate)`` lets the parent panel light up its own
// "Quote Required" badge AND highlight the exact affected components
// using the same server-authoritative data (``quote_required`` +
// ``quote_required_components``) the pricing engine returned — so the
// three on-screen surfaces (list pill, header badge, component chips)
// never disagree. ``gate`` is ``{quote_required, components}``.
function CostPreviewWithBreakdown({ bomProductId, components, getProcurementItemName, onGateChange }) {
  const [costs, setCosts] = useState(null);
  const [quantity, setQuantity] = useState(250);
  const [orderTypePreview, setOrderTypePreview] = useState('');
  const [loading, setLoading] = useState(false);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    const fetchCost = async () => {
      setLoading(true);
      try {
        let url = `${API_URL}/api/bom/products/${bomProductId}/cost?quantity=${quantity}`;
        if (orderTypePreview) url += `&order_type=${orderTypePreview}`;
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCosts(data);
          if (onGateChange) {
            // Surface the server-side gate result (direct + nested) so
            // the parent panel can replace its direct-only badge with
            // the authoritative one. Fires every time quantity /
            // order_type / selectedBom changes.
            onGateChange({
              quote_required: !!data.quote_required,
              components: data.quote_required_components || [],
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch cost:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCost();
  }, [bomProductId, quantity, orderTypePreview]);

  // Create a map of component details from cost calculation
  const componentCostMap = useMemo(() => {
    if (!costs?.components) return {};
    const map = {};
    costs.components.forEach(comp => {
      map[comp.supplier_sku] = comp;
    });
    return map;
  }, [costs]);

  const formatCurrency = (value, currency = 'EUR') => {
    if (value === null || value === undefined) return '—';
    return new Intl.NumberFormat('da-DK', { 
      style: 'decimal', 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    }).format(value);
  };

  return (
    <div className="space-y-4">
      {/* Quantity Selector */}
      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Eye className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Cost Preview</span>
          <select
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
          >
            <option value={100}>100 units</option>
            <option value={250}>250 units</option>
            <option value={300}>300 units</option>
            <option value={500}>500 units</option>
            <option value={1000}>1000 units</option>
            <option value={2000}>2000 units</option>
          </select>
          <select
            value={orderTypePreview}
            onChange={(e) => setOrderTypePreview(e.target.value)}
            className={`border rounded px-2 py-1 text-sm font-medium ${
              orderTypePreview === 'new' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' :
              orderTypePreview === 'reorder' ? 'border-blue-300 bg-blue-50 text-blue-700' :
              'border-gray-300 bg-white text-gray-600'
            }`}
            data-testid="cost-preview-order-type"
          >
            <option value="">All components</option>
            <option value="new">New order</option>
            <option value="reorder">Re-order</option>
          </select>
        </div>
        {loading && <span className="text-sm text-gray-400">Calculating...</span>}
      </div>

      {/* Components Table with Pricing */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Supplier SKU</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Qty Formula</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Calc. Qty</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Unit Cost</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Line Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {components?.map((comp, idx) => {
              const costInfo = componentCostMap[comp.supplier_sku];
              const hasError = costInfo?.error;
              const isQuoteRow = !!costInfo?.requires_quote;

              return (
                <tr
                  key={idx}
                  className={`hover:bg-gray-50 ${hasError ? 'bg-yellow-50' : ''} ${isQuoteRow ? 'bg-yellow-50/60' : ''}`}
                  data-testid={isQuoteRow ? `cost-row-quote-required-${comp.supplier_sku}` : undefined}
                >
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1 flex-wrap">
                      <span>{comp.supplier_sku}</span>
                      {comp.is_fixed_cost && (
                        <span className="px-1 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Fixed</span>
                      )}
                      {comp.applicability && comp.applicability !== 'both' && (
                        <span className={`px-1 py-0.5 text-xs rounded ${
                          comp.applicability === 'new' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {comp.applicability === 'new' ? 'New' : 'Reorder'}
                        </span>
                      )}
                      {isQuoteRow && (
                        // Inline chip — same wording as everywhere else
                        // in the editor + list, so admins recognise the
                        // state at a glance.
                        <span className="px-1 py-0.5 text-[10px] rounded font-semibold bg-yellow-200 text-yellow-900 border border-yellow-300">
                          Quote Required
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-900">
                    {comp.name || getProcurementItemName(comp.supplier_sku)}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500">
                    {comp.quantity_formula}
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-gray-700">
                    {loading ? '...' : (costInfo?.quantity ?? '—')}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">
                    {loading ? '...' : (
                      hasError ? (
                        <span className="text-yellow-600 text-xs">Not found</span>
                      ) : isQuoteRow ? (
                        // Show the pending (stored) tier price as context
                        // but visually gray it out so the admin doesn't
                        // treat it as a confirmed cost.
                        <span className="text-gray-400 italic text-xs">
                          {costInfo?.cost_per_unit_pending != null
                            ? `${formatCurrency(costInfo.cost_per_unit_pending)} (pending)`
                            : 'pending quote'}
                        </span>
                      ) : (
                        costInfo?.cost_per_unit != null ? formatCurrency(costInfo.cost_per_unit) : '—'
                      )
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-gray-900">
                    {loading ? '...' : (
                      hasError ? (
                        <span className="text-yellow-600">—</span>
                      ) : isQuoteRow ? (
                        <span className="text-yellow-700 text-xs font-semibold">Quote Required</span>
                      ) : (
                        costInfo?.line_cost != null ? formatCurrency(costInfo.line_cost) : '—'
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          
          {/* Totals Footer */}
          {costs && !loading && (
            <tfoot className="bg-gray-100 font-medium">
              {costs.quote_required ? (
                // Gate is active — the pricing engine refused to produce
                // a confident total so the UI MUST NOT show one either.
                // Reuse the same wording and amber styling the rest of
                // the editor uses for consistency.
                <tr
                  className="border-t-2 border-yellow-300 bg-yellow-50"
                  data-testid="cost-preview-unresolved-footer"
                >
                  <td colSpan={5} className="px-3 py-3 text-right text-yellow-900 font-semibold">
                    Cost unresolved — supplier quote required
                  </td>
                  <td className="px-3 py-3 text-right text-yellow-900 text-sm font-semibold">
                    {(costs.quote_required_components || []).length} component
                    {(costs.quote_required_components || []).length === 1 ? '' : 's'}
                  </td>
                </tr>
              ) : (
                <>
                  {costs.total_fixed_cost > 0 && (
                    <tr className="border-t border-gray-200">
                      <td colSpan={5} className="px-3 py-2 text-right text-gray-600">
                        Fixed Costs
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">
                        {formatCurrency(costs.total_fixed_cost)}
                      </td>
                    </tr>
                  )}
                  {costs.total_variable_cost > 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-2 text-right text-gray-600">
                        Variable Costs
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">
                        {formatCurrency(costs.total_variable_cost)}
                      </td>
                    </tr>
                  )}
                  <tr className="border-t-2 border-gray-300">
                    <td colSpan={5} className="px-3 py-2 text-right text-gray-900">
                      Total Cost ({quantity} units)
                    </td>
                    <td className="px-3 py-2 text-right text-lg text-gray-900">
                      {costs.currency} {formatCurrency(costs.total_cost)}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="px-3 py-2 text-right text-gray-600">
                      Cost per Unit
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {costs.currency} {formatCurrency(costs.cost_per_unit)}
                    </td>
                  </tr>
                </>
              )}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// BOM Modal Component
function BOMModal({ bom, cloneSource, procurementItems, productFamily = 'vinyl', skuScope = 'current', onSkuScopeChange = null, onSave, onClose }) {
  // `cloneSource` seeds all fields from an existing BOM but keeps the
  // modal in CREATE mode — Product ID stays editable, and Save always
  // POSTs a brand-new BOM. `bom` (when set) means true Edit mode: PATCH
  // on save, Product ID locked to preserve the row's identity.
  const seed = bom || cloneSource || null;
  const [formData, setFormData] = useState({
    product_id: seed?.product_id || '',
    name: seed?.name || '',
    description: seed?.description || '',
    // Phase 2 (May 2026): the BOM-level Category free-text field has been
    // retired. Product Family now drives all of: filtering tabs, SKU-pool
    // scoping, flows, quick bundles, pricing defaults, customer visibility
    // and reporting. We mirror product_family into the legacy `category`
    // column on save so cost calculations and old reports keep working.
    product_family_value: seed?.product_family || productFamily || 'vinyl',
    format: seed?.format || '',
    variant: seed?.variant || '',
  });

  const [components, setComponents] = useState(
    seed?.components?.map((c, i) => ({ ...c, id: i, applicability: c.applicability || 'both' })) || [{ id: 0, supplier_sku: '', name: '', quantity_formula: 'order_quantity', is_fixed_cost: false, applicability: 'both', priority: 1 }]
  );

  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem('token');

  // BOM-scoped procurement item pool — resolves supplier SKUs across the
  // BOM's own product family, NOT the outer UI state's family. Historically
  // opening a Blu-ray BOM while the outer filter was on Vinyl produced an
  // empty SKU picker → users saved the BOM with blank supplier_sku → order
  // cost lines came back as "Unknown / 0" and it looked like the BOM
  // lookup was "restricted to Vinyl". Fetching against the BOM's own
  // family (with automatic 'all' fallback when the specific family has
  // zero items) makes the lookup work for every product family.
  const [modalProcurementItems, setModalProcurementItems] = useState(procurementItems || []);
  const bomFamily = formData.product_family_value;

  // ---- Live "Quote Required" preview --------------------------------
  // Mirrors ``services/quote_gate.collect_quote_required_components_from_bom``
  // on the backend. Direct-level hits are detected client-side from the
  // already-loaded picker payload so the banner updates INSTANTLY as
  // the admin edits the components array (no round-trip needed). A
  // debounced ``POST /bom/quote-check`` call then layers nested-BOM
  // detection on top so sub-BOM references don't silently hide a
  // quote-required leaf. Both signals are informational only — saving
  // the BOM is never blocked.
  const procurementBySku = useMemo(() => {
    const map = new Map();
    for (const item of modalProcurementItems || []) {
      if (item && item.supplier_sku) map.set(item.supplier_sku, item);
    }
    return map;
  }, [modalProcurementItems]);

  const directQuoteRequired = useMemo(() => {
    const seen = new Set();
    const hits = [];
    for (const c of components || []) {
      if (!c.supplier_sku || seen.has(c.supplier_sku)) continue;
      const item = procurementBySku.get(c.supplier_sku);
      if (item && item.is_quotable === false) {
        seen.add(c.supplier_sku);
        hits.push({
          supplier_sku: c.supplier_sku,
          procurement_item_name: item.name || c.supplier_sku,
          applicability: c.applicability || 'both',
          source: 'direct',
        });
      }
    }
    return hits;
  }, [components, procurementBySku]);

  const [nestedQuoteRequired, setNestedQuoteRequired] = useState([]);
  const [quoteCheckLoading, setQuoteCheckLoading] = useState(false);

  useEffect(() => {
    // Only call the server when there's at least one component whose
    // supplier_sku is NOT in the direct picker pool — that's the
    // signature of a possible sub-BOM reference the client can't
    // resolve. Keeps the editor idle for simple flat BOMs.
    const unresolved = (components || []).filter(
      (c) => c.supplier_sku && !procurementBySku.has(c.supplier_sku)
    );
    if (unresolved.length === 0) {
      setNestedQuoteRequired([]);
      return;
    }
    const payload = {
      components: (components || [])
        .filter((c) => c.supplier_sku)
        .map((c) => ({
          supplier_sku: c.supplier_sku,
          name: c.name,
          quantity_formula: c.quantity_formula || 'order_quantity',
          applicability: c.applicability || 'both',
        })),
    };
    let cancelled = false;
    const timer = setTimeout(async () => {
      setQuoteCheckLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/bom/quote-check`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) return;
        const body = await res.json();
        if (cancelled) return;
        // Keep only the hits that are NOT already in the direct list
        // (those came from a nested sub-BOM — surface them separately).
        const directSkus = new Set(directQuoteRequired.map((d) => d.supplier_sku));
        setNestedQuoteRequired(
          (body.components || []).filter((h) => !directSkus.has(h.supplier_sku))
        );
      } catch {
        if (!cancelled) setNestedQuoteRequired([]);
      } finally {
        if (!cancelled) setQuoteCheckLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [components, procurementBySku, directQuoteRequired]);

  const allQuoteRequired = useMemo(
    () => [...directQuoteRequired, ...nestedQuoteRequired],
    [directQuoteRequired, nestedQuoteRequired]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchFamilyItems = async () => {
      try {
        const familyParam = skuScope === 'current' ? bomFamily : skuScope;
        const primary = await fetch(
          `${API_URL}/api/pricing/items?limit=500&product_family=${encodeURIComponent(familyParam)}`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        if (!primary.ok) return;
        const data = await primary.json();
        let items = (data.items || []).map(item => ({
          ...item,
          supplier_sku: item.sku, // map sku → supplier_sku for BOM compat
        }));
        // Fallback: if the specific family has zero procurement items,
        // widen to `all` so admins can still attach a cross-family SKU
        // (common early in a new family's lifecycle when its catalog is
        // sparse — the alternative is a completely empty picker).
        if (items.length === 0 && familyParam !== 'all') {
          const fallback = await fetch(
            `${API_URL}/api/pricing/items?limit=500&product_family=all`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          if (fallback.ok) {
            const fdata = await fallback.json();
            items = (fdata.items || []).map(item => ({
              ...item,
              supplier_sku: item.sku,
            }));
          }
        }
        if (!cancelled) setModalProcurementItems(items);
      } catch (e) {
        // Swallow — the modal falls back to the outer prop pool, which is
        // at worst a stale but non-empty list.
        if (!cancelled) setModalProcurementItems(procurementItems || []);
      }
    };
    fetchFamilyItems();
    return () => { cancelled = true; };
    // Re-run whenever the BOM's own family or the user's scope changes so
    // switching family in the form immediately re-scopes the picker.
  }, [bomFamily, skuScope]);

  const addComponent = () => {
    setComponents([
      ...components,
      { id: Date.now(), supplier_sku: '', name: '', quantity_formula: 'order_quantity', is_fixed_cost: false, applicability: 'both', priority: components.length + 1 }
    ]);
  };

  const removeComponent = (id) => {
    if (components.length > 1) {
      setComponents(components.filter(c => c.id !== id));
    }
  };

  const updateComponent = (id, field, value) => {
    setComponents(components.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const method = bom ? 'PATCH' : 'POST';
      const url = bom 
        ? `${API_URL}/api/bom/products/${bom.id}`
        : `${API_URL}/api/bom/products`;
      
      const { product_family_value, ...restForm } = formData;
      const payload = {
        ...restForm,
        // Mirror Product Family into the legacy `category` column so legacy
        // reports / cost queries that filter by `category=='vinyl'` still match.
        category: product_family_value,
        product_family: product_family_value,
        components: components.map(({ id, ...rest }) => rest),
      };
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] flex flex-col">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-medium text-gray-900">
            {bom ? 'Edit Product BOM' : cloneSource ? 'Clone Product BOM (new copy)' : 'New Product BOM'}
          </h3>
          {cloneSource && (
            <p className="text-xs text-slate-500 mt-1">
              Saving will create a brand-new BOM. The original ({cloneSource.product_id}) will not be modified.
            </p>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto overflow-x-visible p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product ID</label>
              <input
                type="text"
                value={formData.product_id}
                onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono"
                placeholder="12-standard-black"
                required
                disabled={!!bom}
                data-testid="bom-product-id-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder='12" Standard Black LP'
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Family <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.product_family_value}
                onChange={(e) => setFormData({ ...formData, product_family_value: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                required
                data-testid="bom-product-family"
              >
                <option value="vinyl">Vinyl</option>
                <option value="cd">CD</option>
                <option value="dvd">DVD</option>
                <option value="bluray">Blu-ray</option>
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                Drives filtering tabs, SKU pool, flows, bundles, customer visibility.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
              <input
                type="text"
                value={formData.format}
                onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Variant</label>
              <input
                type="text"
                value={formData.variant}
                onChange={(e) => setFormData({ ...formData, variant: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="standard"
              />
            </div>
          </div>
          
          {/* Components */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Components</label>
              <div className="flex items-center gap-3">
                {onSkuScopeChange && (
                  <FamilyScopePicker
                    value={skuScope}
                    onChange={onSkuScopeChange}
                    activeFamily={productFamily}
                    label="SKU pool"
                  />
                )}
                <button
                  type="button"
                  onClick={addComponent}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  + Add Component
                </button>
              </div>
            </div>

            {/* Quote-Required banner — informational only. Reuses the
                same backend logic the pricing engine uses so the
                preview here and the live Request-a-Quote gate never
                disagree. Does NOT block saving. */}
            {allQuoteRequired.length > 0 && (
              <div
                className="mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900"
                data-testid="bom-quote-required-banner"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                <div className="text-xs leading-relaxed">
                  <div className="font-semibold">
                    {allQuoteRequired.length} quote-required component
                    {allQuoteRequired.length === 1 ? '' : 's'}
                    {quoteCheckLoading && (
                      <span className="ml-1 font-normal text-amber-700">(resolving…)</span>
                    )}
                  </div>
                  <div className="mt-0.5">
                    Any order configured to use this BOM will enter the Request-a-Quote
                    workflow. Flip the item's "Quotable" switch after obtaining a
                    supplier price to clear it.
                  </div>
                  <ul className="mt-1 list-disc pl-5 space-y-0.5">
                    {allQuoteRequired.map((h) => (
                      <li
                        key={`${h.supplier_sku}-${h.from_bom || 'direct'}`}
                        data-testid={`bom-quote-required-item-${h.supplier_sku}`}
                      >
                        <span className="font-mono">{h.supplier_sku}</span>
                        {h.procurement_item_name ? ` — ${h.procurement_item_name}` : ''}
                        {h.from_bom && (
                          <span className="text-amber-700">
                            {' '}(via sub-BOM <span className="font-mono">{h.from_bom}</span>)
                          </span>
                        )}
                        {h.applicability && h.applicability !== 'both' && (
                          <span className="text-amber-700"> · {h.applicability} only</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <div className="space-y-2 border border-border rounded-lg p-3">
              {components.map((comp, idx) => (
                <div key={comp.id} className="flex items-center gap-2">
                  <input
                    type="number"
                    value={comp.priority}
                    onChange={(e) => updateComponent(comp.id, 'priority', parseInt(e.target.value) || idx + 1)}
                    className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
                    placeholder="#"
                  />
                  <SKUSelector
                    value={comp.supplier_sku}
                    items={modalProcurementItems}
                    productFamily={skuScope === 'current' ? bomFamily : skuScope}
                    activeFamily={bomFamily}
                    onChange={(sku, item) => {
                      setComponents(prev => prev.map(c => 
                        c.id === comp.id 
                          ? { ...c, supplier_sku: sku, name: item?.name || c.name }
                          : c
                      ));
                    }}
                    placeholder="Select SKU..."
                  />
                  <select
                    value={comp.quantity_formula}
                    onChange={(e) => updateComponent(comp.id, 'quantity_formula', e.target.value)}
                    className="w-40 border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="order_quantity">order_quantity</option>
                    <option value="1">1 (fixed)</option>
                    <option value="2">2</option>
                    <option value="order_quantity * 2">qty × 2</option>
                    <option value="order_quantity * 3">qty × 3</option>
                    <option value="order_quantity * 4">qty × 4</option>
                    <option value="order_quantity * 5">qty × 5</option>
                  </select>
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={comp.is_fixed_cost}
                      onChange={(e) => updateComponent(comp.id, 'is_fixed_cost', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Fixed
                  </label>
                  <select
                    value={comp.applicability || 'both'}
                    onChange={(e) => updateComponent(comp.id, 'applicability', e.target.value)}
                    className={`w-24 border rounded px-1.5 py-1 text-xs font-medium ${
                      comp.applicability === 'new' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' :
                      comp.applicability === 'reorder' ? 'border-blue-300 bg-blue-50 text-blue-700' :
                      'border-gray-300 text-gray-600'
                    }`}
                    data-testid={`comp-applicability-${idx}`}
                  >
                    <option value="both">Both</option>
                    <option value="new">New only</option>
                    <option value="reorder">Reorder</option>
                  </select>
                  {components.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeComponent(comp.id)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <Trash2 className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save BOM'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
